/**
 * Read-only questions about a graph, used by the inspector: who builds on whom, which nodes
 * are used most, how many nodes per category, what a group holds... Pure functions, no state.
 */
import type { FieldDef } from "@protocol/FieldDef";
import type { Graph } from "@protocol/Graph";
import type { Kinds } from "@protocol/Kinds";
import type { Group } from "@protocol/Group";
import type { Link } from "@protocol/Link";
import type { LinkKind } from "@protocol/LinkKind";
import type { Node } from "@protocol/Node";
import { categoryCounts } from "./categories";
import { shortValues, valueCounts } from "./fields";
import { linkKindOf } from "./kinds";

/** A neighbour of a node, with the link that joins them. */
export interface Neighbour {
  node: Node;
  link: Link;
}

/** Indexes nodes by id. */
export function byId(graph: Graph): Map<string, Node> {
  return new Map(graph.nodes.map((n) => [n.id, n]));
}

/** Nodes `id` points at through links of `kind` ("what it implements"). */
export function outgoing(graph: Graph, id: string, kind: LinkKind): Neighbour[] {
  const nodes = byId(graph);
  return graph.links
    .filter((l) => l.from === id && l.kind === kind)
    .flatMap((link) => {
      const node = nodes.get(link.to);
      return node ? [{ node, link }] : [];
    });
}

/** Nodes pointing at `id` through links of `kind` ("what extends it"). */
export function incoming(graph: Graph, id: string, kind: LinkKind): Neighbour[] {
  const nodes = byId(graph);
  return graph.links
    .filter((l) => l.to === id && l.kind === kind)
    .flatMap((link) => {
      const node = nodes.get(link.from);
      return node ? [{ node, link }] : [];
    });
}

/** The parents of `id` following `kind` links upwards (`inherits`): `[parent, grandparent, ...]`. */
export function inheritanceChain(graph: Graph, id: string, kind = "inherits"): Node[] {
  const nodes = byId(graph);
  const chain: Node[] = [];
  const seen = new Set([id]);
  let current = id;
  for (;;) {
    const up = graph.links.find((l) => l.from === current && l.kind === kind);
    if (!up || seen.has(up.to)) break;
    const parent = nodes.get(up.to);
    if (!parent) break;
    chain.push(parent);
    seen.add(up.to);
    current = up.to;
  }
  return chain;
}

/** A node with a count, for the overview lists. */
export interface Counted {
  node: Node;
  count: number;
}

/** Summary shown when nothing is selected. */
export interface Overview {
  /** The nodes most built upon (the ends of hierarchy links: extends, implements...), most first (max 6). */
  builtUpon: Counted[];
  /** The nodes most linked to by the other links (uses, depends...), most first (max 5). */
  mostUsed: Counted[];
  /** Number of nodes per category, in the graph's order of categories. */
  perCategory: { name: string; count: number }[];
  /** For each choice field of the environment (a language), how many nodes hold each value, most first. */
  values: { field: FieldDef; values: { value: string; count: number }[] }[];
}

/**
 * Computes the overview of `graph`, whose link kinds are described by `kinds` (which links
 * build a hierarchy) and whose nodes carry the `fields` of their environment.
 */
export function overview(graph: Graph, kinds: Kinds | undefined, fields: readonly FieldDef[] = []): Overview {
  const nodes = byId(graph);
  /** Counts, per target node, the links that `keep` accepts. */
  const countBy = (keep: (l: Link) => boolean) => {
    const counts = new Map<string, number>();
    for (const link of graph.links) if (keep(link)) counts.set(link.to, (counts.get(link.to) ?? 0) + 1);
    return [...counts]
      .flatMap(([id, count]) => {
        const node = nodes.get(id);
        return node ? [{ node, count }] : [];
      })
      .sort((a, b) => b.count - a.count || a.node.name.localeCompare(b.node.name));
  };
  /** Whether each link kind builds a hierarchy, looked up once per kind (a big graph has 80 000 links). */
  const tones = new Map<string, boolean>();
  /** `true` when a link builds a hierarchy (its kind may not go round in a circle). */
  const hierarchy = (l: Link) => {
    let yes = tones.get(l.kind);
    if (yes === undefined) tones.set(l.kind, (yes = linkKindOf(kinds, l).tone === "hierarchy"));
    return yes;
  };
  return {
    builtUpon: countBy(hierarchy).slice(0, 6),
    mostUsed: countBy((l) => !hierarchy(l)).slice(0, 5),
    perCategory: categoryCounts(graph),
    values: valueCounts(graph.nodes, fields),
  };
}

/** `true` when `node` matches a search (its name or one of its short values, a language; case-insensitive). */
export function matchesQuery(node: Pick<Node, "name" | "data">, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  return node.name.toLowerCase().includes(q) || shortValues(node).some((v) => v.toLowerCase().includes(q));
}

/** The best match for a search: the node whose name contains the query earliest. */
export function bestMatch(nodes: Node[], query: string): Node | undefined {
  const q = query.toLowerCase();
  /** How well a node matches the query (lower is better). */
  const rank = (n: Node) => {
    const at = n.name.toLowerCase().indexOf(q);
    return at < 0 ? 999 : at;
  };
  return nodes.filter((n) => matchesQuery(n, q)).sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name))[0];
}

/** The groups directly inside `id` (`null`: the top-level groups). */
export function subGroups(graph: Graph, id: string | null): Group[] {
  return graph.groups.filter((g) => (g.parent ?? null) === id);
}

/** `id` and every group inside it, at any depth (a group cannot move into one of these). */
export function groupAndDescendants(graph: Graph, id: string): Set<string> {
  const found = new Set([id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const g of graph.groups) {
      if (g.parent && found.has(g.parent) && !found.has(g.id)) {
        found.add(g.id);
        grew = true;
      }
    }
  }
  return found;
}

/** The nodes in group `id`: directly, or with `deep` also in the groups inside it. */
export function nodesInGroup(graph: Graph, id: string, deep = false): Node[] {
  const groups = deep ? groupAndDescendants(graph, id) : new Set([id]);
  return graph.nodes.filter((n) => n.group != null && groups.has(n.group));
}

/** The groups from the top level down to `id` (included), for a breadcrumb: Backend › Modules. */
export function groupPath(graph: Graph, id: string): Group[] {
  const path: Group[] = [];
  const seen = new Set<string>();
  for (let g = graph.groups.find((x) => x.id === id); g && !seen.has(g.id); g = graph.groups.find((x) => x.id === g!.parent)) {
    seen.add(g.id);
    path.unshift(g);
  }
  return path;
}

/** A group offered in a picker: its id, and its name indented by how deep it sits. */
export interface GroupChoice {
  id: string;
  label: string;
  depth: number;
}

/** Every group in tree order (each one followed by the groups inside it), minus `exclude`. */
export function groupChoices(graph: Graph, exclude: Set<string> = new Set()): GroupChoice[] {
  const choices: GroupChoice[] = [];
  const seen = new Set<string>();
  /** Adds the groups inside `parent`, then theirs. */
  const visit = (parent: string | null, depth: number) => {
    for (const g of subGroups(graph, parent)) {
      if (seen.has(g.id)) continue;
      seen.add(g.id);
      if (!exclude.has(g.id)) choices.push({ id: g.id, label: `${"  ".repeat(depth)}${g.name}`, depth });
      visit(g.id, depth + 1);
    }
  };
  visit(null, 0);
  return choices;
}
